<template>
  <stop-display :location-name="currentLocationName" :prior-location-name="priorLocationName">
    <template #top-right>
      <v-tooltip v-if="isStopComplete" :open-delay="250" content-class="solid-tooltip" top>
        <template #activator="{ on }">
          <v-icon data-test="stop-icon" :color="stopIconColor" class="mt-1" v-on="on">{{
            stopIcon
          }}</v-icon>
        </template>
        <span data-test="stop-icon-tooltip">{{ stopIconTooltip }}</span>
      </v-tooltip>
    </template>
    <template #cards>
      <ReceiptCards
        v-if="stop.inventoryReceipts.length > 0"
        :stop="stopRef"
        @click="onReceiptClick"
      />
      <JobActualStopCard v-if="stopRef.isSkipped" :stop="stopRef" @click="onSkippedCardClick" />
      <span v-else>
        <stop-card
          v-for="form in stopForms"
          :key="form.formUuid"
          :icon="form.isFilledOut ? 'mdi-clipboard-check-outline' : 'mdi-clipboard-text-outline'"
          :state="form.isFilledOut ? 'completed' : 'incomplete'"
          :title="form.name"
          data-test="stop-form-card"
          @click="() => fillOutForm(form)"
        />
        <disabled-action-tooltip
          v-if="shouldShowReceiverReceiptButton"
          :permission="receiverReceiptPermission"
          label="Confirm receipt"
        >
          <template #default="{ disabled }">
            <vs-button
              type="secondary"
              data-test="add-receipt-button"
              small
              icon="mdi-receipt-text-outline"
              label="Confirm receipt"
              class="mt-2"
              :disabled="disabled"
              @click="onAddLocationOwnerReceiptClick"
            />
          </template>
        </disabled-action-tooltip>
      </span>
    </template>
  </stop-display>
</template>

<script setup lang="ts">
import ReceiptCards from '@/components/tickets/StopsAndInventories/ReceiptCards.vue'
import VsButton from '@/components/vision/VsButton.vue'
import DisabledActionTooltip from '@/components/common/DisabledActionTooltip.vue'
import { InventoryReceipt, ActualStop, RequestedStop, InventoryReceiptType } from '@ironsight/types'
import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'
import { computed } from 'vue'
import { useJobStopsVisibilityLogic } from '@/components/tickets/StopsAndInventories/useJobStopsVisibilityLogic'
import StopCard from '@/components/tickets/StopsAndInventories/StopCard.vue'
import { useJobStopForms } from '@/components/tickets/StopsAndInventories/useJobStopForms'
import StopDisplay from '@/components/tickets/StopsAndInventories/StopDisplay.vue'
import JobActualStopCard from './JobActualStopCard.vue'
import {
  isRequestedStopCompleted,
  StopCompletionStatus,
} from '@/components/tickets/StopsAndInventories/useIsRequestedStopCompleted'

const props = defineProps<{
  requestedStop: RequestedStop | null
  stop: ActualStop
  jobNumber: number
}>()

const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()

const stopRef = computed(() => props.stop)
const requestedStopRef = computed(() => props.requestedStop)
const jobNumberRef = computed(() => props.jobNumber)

const { getFormsForStop } = useJobStopForms(jobNumberRef)
const stopForms = computed(() => getFormsForStop(props.stop))
const isStopComplete = computed(
  () => stopForms.value.every((fs) => fs.isFilledOut) || stopRef.value.isSkipped,
)

const currentLocationName = computed(() => {
  if (stopRef.value.isSkipped) {
    return requestedStopRef.value?.locationName ?? 'Unknown location'
  }
  return stopRef.value.locationName ?? 'Unknown location'
})

const priorLocationName = computed(() => {
  if (
    requestedStopRef.value &&
    requestedStopRef.value.locationId !== stopRef.value.locationId &&
    !stopRef.value.isSkipped
  ) {
    return requestedStopRef.value.locationName
  }

  return null
})

const isStopCompleted = computed<StopCompletionStatus>(() => {
  if (!requestedStopRef.value) {
    return {
      locationMatches: false,
      inventoryMatches: false,
    }
  }
  return isRequestedStopCompleted(requestedStopRef.value, stopRef.value)
})

const doesActualEqualRequested = computed(
  () => isStopCompleted.value.locationMatches && isStopCompleted.value.inventoryMatches,
)

const stopIcon = computed(() => {
  if (doesActualEqualRequested.value) {
    return 'mdi-check-circle-outline'
  }

  return 'mdi-alert-circle-outline'
})

const stopIconColor = computed(() => {
  if (stopRef.value.isSkipped) {
    return 'red'
  }

  return doesActualEqualRequested.value ? 'green' : 'orange'
})

const stopIconTooltip = computed(() => {
  if (stopRef.value.isSkipped) {
    return 'Stop has been skipped. No activity at this stop.'
  } else if (doesActualEqualRequested.value) {
    return 'Stop has been completed successfully.'
  } else if (!isStopCompleted.value.locationMatches && !isStopCompleted.value.inventoryMatches) {
    return 'The confirmed location and inventory do not match the requested location and inventory.'
  } else if (!isStopCompleted.value.locationMatches) {
    return 'The confirmed location does not match the requested location.'
  } else if (!isStopCompleted.value.inventoryMatches) {
    return 'The confirmed inventory does not match the requested inventory.'
  } else {
    return 'The confirmed stop does not match the requested stop.'
  }
})

const onReceiptClick = (receipts: InventoryReceipt[], receiptType: InventoryReceiptType): void => {
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    receiptDetailsBottomSheetStates: {
      isVisible: true,
      actualStop: stopRef.value,
      receipts,
      receiptType,
    },
  })
}

const onSkippedCardClick = () => {
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    actualSkippedStopDetailsBottomSheetStates: {
      isVisible: true,
      actualStop: stopRef.value,
    },
  })
}

const onAddLocationOwnerReceiptClick = () => {
  if (!canConfirmReceiverReceipt.value) return
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    stopConfirmationBottomSheetStates: {
      isVisible: true,
      requestedStop: null,
      actualStop: stopRef.value,
      receiptType: InventoryReceiptType.Receiver,
    },
  })
}

const { shouldShowReceiverReceiptButton, receiverReceiptPermission, canConfirmReceiverReceipt } =
  useJobStopsVisibilityLogic(stopRef, jobNumberRef)

const fillOutForm = (form) => {
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    formSubmissionBottomSheetState: {
      isVisible: true,
      isEditing: !form.isFilledOut,
      formUuid: form.formUuid,
      formSubmissionUuid: form.isFilledOut ? form.formSubmissionUuid : null,
      stopId: props.stop.id!,
    },
  })
}
</script>

<style scoped>
:global(.v-tooltip__content.solid-tooltip) {
  opacity: 1 !important;
}
</style>
